import { formatDate } from 'date-fns'
import { NaiveDate } from 'lib/utilities/dateTime'
import { ReactNode } from 'react'

export interface ExtractShipmentDataProps {
  lines: Array<{
    label: string
    value: string | ReactNode
  }>
}
interface LegWithTrip extends OrderLeg {
  roundtrip?: boolean
}

/**
 * Get concatenated address segments for comparison
 */
export function getAddressSegments(
  address: OrderAddress,
  splitAddresses: boolean = false,
) {
  return splitAddresses
    ? [
        address.address_1,
        address.address_2,
        [address.city, address.state, address.country]
          .filter(Boolean)
          .join(', '),
      ]
    : [
        address.address_1,
        address.address_2,
        address.city,
        address.state,
        address.country,
      ]
        .filter(Boolean)
        .join(', ')
}

/**
 * Check if legs is roundtrip
 *
 * If we have multiple legs, and each leg has the same (forward) and reverse
 * origin and destination, we can assume they are a roundtrip
 * and we can filter out the succeeding leg that has backward trip
 */
export function isRoundTrip(legs: OrderLeg[]) {
  let isRoumdTrip = false
  legs.forEach((leg) => {
    const { origin, destination } = leg

    const originAddress = getAddressSegments(origin)
    const destinationAddress = getAddressSegments(destination)
    isRoumdTrip = legs.some(
      (innerLeg) =>
        innerLeg !== leg &&
        originAddress === getAddressSegments(innerLeg.destination) &&
        destinationAddress === getAddressSegments(innerLeg.origin),
    )
  })
  return isRoumdTrip
}

/**
 * Filter out succeeding roundtrip legs
 *
 * If we have multiple legs, and each leg has the same (forward) and reverse
 * origin and destination, we can assume they are a roundtrip
 * and we can filter out the succeeding leg that has backward trip
 */
export function filterRoundTripLegs(legs?: OrderLeg[]) {
  if (!legs?.length) return []

  const filteredLegs: LegWithTrip[] = []

  // Set to keep track of origins and destinations that have been seen
  const seen = new Set<string>()

  legs.forEach((leg) => {
    const { origin, destination } = leg

    const originAddress = getAddressSegments(origin)
    const destinationAddress = getAddressSegments(destination)

    const originKey = `${originAddress}-${destinationAddress}`
    const reverseKey = `${destinationAddress}-${originAddress}`

    if (!seen.has(originKey) && !seen.has(reverseKey)) {
      // Determine if there is a roundtrip counterpart
      const roundtrip = legs.some(
        (innerLeg) =>
          innerLeg !== leg &&
          originAddress === getAddressSegments(innerLeg.destination) &&
          destinationAddress === getAddressSegments(innerLeg.origin),
      )

      filteredLegs.push({
        ...leg,
        roundtrip,
      })

      seen.add(originKey)
      seen.add(reverseKey)
    }
  })

  return filteredLegs
}

/**
 * Count occurrences of items
 *
 * input: ['apple', 'orange', 'mango', 'pear', 'orange', 'apple', 'apple']
 * output: ['3 apple', '2 orange', '1 mango', '1 pear']
 */
export function countItems(items?: string[]) {
  if (!items?.length) return []

  // Reduce count occurrences of each item
  const countMap = items.reduce((acc, item) => {
    if (acc.has(item)) {
      acc.set(item, acc.get(item) + 1)
    } else {
      acc.set(item, 1)
    }

    return acc
  }, new Map())

  return Array.from(countMap, ([item, count]) => `${count} ${item}`)
}

/**
 * Get shipment and delivery date from order leg data
 *
 * Accepts: Order leg data
 * Output: lines: [
      { label: 'Pickup', value: pickup_date },
      { label: 'Deliver', value: delivery_date },
    ]
 */
export function getShipmentLevelPickupAndDeliveryDate(
  orderLeg: OrderLeg,
): ExtractShipmentDataProps {
  const pickup_or_dropoff_date = orderLeg
    ? formatDate(
        new NaiveDate(orderLeg?.travelers[0].shipments[0].ship_date || ''),
        'EEE. MM/dd/yyyy',
      )
    : ''
  const delivery_date = orderLeg
    ? formatDate(
        new NaiveDate(
          orderLeg?.travelers[0].shipments[0].estimated_carrier_delivery_date ||
            '',
        ),
        'EEE. MM/dd/yyyy',
      )
    : ''

  const pickup_or_dropoff_label = orderLeg?.travelers[0]?.shipments[0]
    ?.is_dropoff
    ? 'Dropoff'
    : 'Pickup'

  return {
    lines: [
      {
        label: '',
        value:
          orderLeg?.travelers[0]?.shipments[0]
            ?.humanized_carrier_service_type || '',
      },
      { label: pickup_or_dropoff_label, value: pickup_or_dropoff_date },
      { label: 'Deliver', value: delivery_date },
    ],
  }
}
